import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "@/shared/brand";
import { Button } from "@/shared/components/ui/button";
import { Field } from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import { useDocumentTitle } from "@/shared/i18n";
import { AuthCard } from "./auth-card";
import {
  AUTH_INPUT_STYLES,
  AuthFieldError,
  AuthFieldLabel,
  AuthSubmitButton,
  PasswordInput,
} from "./auth-fields";
import { useLastEmailStore } from "./last-email-store";
import { createSignInSchema, type SignInInput } from "./schema";

const FORM_ID = "sign-in";
const EMAIL_ID = `${FORM_ID}-email`;
const EMAIL_ERROR_ID = `${EMAIL_ID}-error`;
const PASSWORD_ID = `${FORM_ID}-password`;
const PASSWORD_ERROR_ID = `${PASSWORD_ID}-error`;

type SignInFormProps = {
  /** Runs the gcip-iap sign-in; rejects with a Firebase Auth error on failure. */
  onSignIn: (email: string, password: string) => Promise<unknown>;
  /** Switch to the reset-password view. */
  onForgotPassword: () => void;
};

/** Sign-in card: email/password form + forgot-password link. */
export function SignInForm({ onSignIn, onForgotPassword }: SignInFormProps) {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("pageTitles.signIn"));
  // In-flow carry-over: prefill the email the user already typed (e.g.
  // returning from reset-password).
  const lastEmail = useLastEmailStore((s) => s.email);
  const setLastEmail = useLastEmailStore((s) => s.setEmail);

  const schema = useMemo(() => createSignInSchema(t), [t]);
  const form = useForm<SignInInput>({
    resolver: standardSchemaResolver(schema),
    defaultValues: { email: lastEmail, password: "" },
    mode: "onSubmit",
  });

  const [email, password] = form.watch(["email", "password"]);
  // trim matches the schema, so the active look never promises a value
  // that validation would reject as empty
  const isFilled = email.trim().length > 0 && password.length > 0;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async (values) => {
    setLastEmail(values.email);
    try {
      // gcip-iap resolves the IAP handshake and redirects on success; this
      // component typically unmounts before the promise settles. On failure
      // App has already mapped the Firebase error via humanizeAuthError, so the
      // thrown message is display-ready here.
      await onSignIn(values.email, values.password);
    } catch (error) {
      form.setError("password", {
        type: "server",
        message:
          error instanceof Error
            ? error.message
            : t("signIn.errors.invalidCredentials"),
      });
    }
  });

  return (
    <AuthCard>
      <div className="flex flex-col gap-4">
        <BrandLogo className="h-6 self-start" />
        <h1 className="text-heading-medium tracking-[-0.56px] text-foreground">
          {t("signIn.title")}
        </h1>
      </div>

      {/* TODO(confirm): password-only tenant, Google CTA removed */}

      <form
        id={FORM_ID}
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-4">
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <AuthFieldLabel htmlFor={EMAIL_ID}>
                  {t("signIn.emailLabel")}
                </AuthFieldLabel>
                <Input
                  {...field}
                  id={EMAIL_ID}
                  type="email"
                  autoComplete="username"
                  placeholder={t("signIn.emailPlaceholder")}
                  aria-invalid={fieldState.invalid}
                  aria-describedby={
                    fieldState.invalid ? EMAIL_ERROR_ID : undefined
                  }
                  className={AUTH_INPUT_STYLES}
                />
                {fieldState.error?.message && (
                  <AuthFieldError id={EMAIL_ERROR_ID}>
                    {fieldState.error.message}
                  </AuthFieldError>
                )}
              </Field>
            )}
          />
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <AuthFieldLabel htmlFor={PASSWORD_ID}>
                  {t("signIn.passwordLabel")}
                </AuthFieldLabel>
                <PasswordInput
                  {...field}
                  id={PASSWORD_ID}
                  autoComplete="current-password"
                  placeholder={t("signIn.passwordPlaceholder")}
                  aria-invalid={fieldState.invalid}
                  aria-describedby={
                    fieldState.invalid ? PASSWORD_ERROR_ID : undefined
                  }
                />
                {fieldState.error?.message && (
                  <AuthFieldError id={PASSWORD_ERROR_ID}>
                    {fieldState.error.message}
                  </AuthFieldError>
                )}
              </Field>
            )}
          />
        </div>

        {/* Stash the typed email so reset-password can prefill it. */}
        <Button
          variant="ghost"
          type="button"
          className="h-10 self-start rounded-full px-4 text-label-small text-muted-foreground"
          onClick={() => {
            setLastEmail(form.getValues("email").trim());
            onForgotPassword();
          }}
        >
          {t("signIn.forgotPassword")}
        </Button>

        <AuthSubmitButton active={isFilled} submitting={isSubmitting}>
          {t("signIn.submit")}
        </AuthSubmitButton>
      </form>
    </AuthCard>
  );
}
